import { convertToParamMap, Params } from '@angular/router';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { getActiveFairTimezone } from '@planone/bau365/data-access-fair-data';
import { selectQueryParams } from '@planone/shared/util-ngrx';

import { dataAccessSearchAdapter } from './data-access-search.reducer';
import { DATA_ACCESS_SEARCH_RESULTS_FEATURE_KEY, State } from './data-access-search.reducer';

import { SearchFilters, SearchSortOrder, SearchType } from '../../model/search-filter';
import { SearchResolver } from '../../search-resolver/search.resolver';
import { resolveDateFilterParam } from '../../service/search-api.utils';

export const getDataAccessSearchState = createFeatureSelector<State>(DATA_ACCESS_SEARCH_RESULTS_FEATURE_KEY);

const { selectAll } = dataAccessSearchAdapter.getSelectors();

export const getSearchResults = createSelector(getDataAccessSearchState, (state: State) => selectAll(state));
export const getSearchResultSuggestions = createSelector(
  getDataAccessSearchState,
  (state: State) => state.resultSuggestions
);

export const getSearchTypeFilter = createSelector(selectQueryParams, (queryParams: Params) => {
  const searchTypes = convertToParamMap(queryParams)
    .getAll(SearchResolver.searchTypeParam)
    .filter((param) => Object.values(SearchType).includes(param as SearchType)) as SearchType[];
  if (searchTypes.length > 0) {
    return searchTypes;
  } else {
    return [SearchType.all];
  }
});

export const getSearchTermFilter = createSelector(selectQueryParams, (queryParams: Params) => {
  if (queryParams && queryParams[SearchResolver.searchTermParam]) {
    return queryParams[SearchResolver.searchTermParam] as string;
  } else {
    return null;
  }
});

export const activeSearchTerm = createSelector(
  getDataAccessSearchState,
  getSearchTermFilter,
  (state: State, searchTerm) => {
    if (state.suggestedSearchTerm) return state.suggestedSearchTerm;
    return searchTerm;
  }
);

export const getSuggestedSearchTerm = createSelector(
  getDataAccessSearchState,
  (state: State) => state.suggestedSearchTerm
);

export const getSearchPagination = createSelector(selectQueryParams, (queryParams: Params) => {
  if (queryParams && queryParams[SearchResolver.searchPageParam]) {
    return queryParams[SearchResolver.searchPageParam] as string;
  } else {
    return null;
  }
});

export const getSearchSortingFilter = createSelector(
  selectQueryParams,
  (queryParams: Params): SearchType | SearchSortOrder => {
    if (queryParams && queryParams[SearchResolver.searchSortParam]) {
      return queryParams[SearchResolver.searchSortParam] as SearchType;
    } else {
      return SearchSortOrder.recommended;
    }
  }
);

export const getSearchStringFilter = createSelector(getDataAccessSearchState, (state: State) => state.stringFilters);

export const getSearchDateFilter = createSelector(
  selectQueryParams,
  getActiveFairTimezone,
  (queryParams: Params, timezone: string) => {
    if (queryParams && queryParams[SearchResolver.searchDateFilterParam]) {
      return resolveDateFilterParam(queryParams[SearchResolver.searchDateFilterParam], timezone);
    } else {
      return null;
    }
  }
);

export const getSearchFilters = createSelector(
  getSearchTypeFilter,
  getSearchTermFilter,
  getSearchSortingFilter,
  getSearchPagination,
  getSearchStringFilter,
  getSearchDateFilter,
  (searchType, searchTerm, searchSorting, page, stringFilter, dateFilter) => {
    return {
      searchTypes: searchType,
      searchTerm,
      page,
      searchSort: searchSorting,
      stringFilter,
      dateFilter,
    } as SearchFilters;
  }
);

export const getCounts = createSelector(getDataAccessSearchState, (state: State) => state.counts);

export const getTotalCounts = createSelector(getDataAccessSearchState, (state: State) => state.total_counts);

export const getNumberOfSearchResults = createSelector(getSearchTypeFilter, getCounts, (searchType, counts) => {
  if (!counts) {
    return 0;
  }
  return searchType.reduce((count, type) => {
    let countForType = 0;
    switch (type) {
      case SearchType.all:
        countForType = counts?.all ?? 0;
        break;
      case SearchType.product:
        countForType = counts?.products ?? 0;
        break;
      case SearchType.event:
        countForType = counts?.events ?? 0;
        break;
      case SearchType.post:
        countForType = counts?.posts ?? 0;
        break;
      case SearchType.exhibitor:
        countForType = counts?.exhibitors ?? 0;
        break;
    }
    return count + countForType;
  }, 0);
});

export const getSearchResultById = createSelector(
  getDataAccessSearchState,
  (state: State, props: { itemId?: string }) => {
    if (props.itemId && state.entities[props.itemId]) {
      return state.entities[props.itemId];
    }
    return undefined;
  }
);

export const isLoaded = createSelector(getDataAccessSearchState, (state: State) => state.loaded);

export const isSearchFilterModalOpen = createSelector(getDataAccessSearchState, (state: State) => {
  return !!state?.searchFilterModalOpen;
});

export const getAutocompleteSuggestions = createSelector(getDataAccessSearchState, (state: State) => {
  return state.autocompleteSuggestions;
});
